import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface CheckRow {
  id: number;
  vessel_id: number;
  vessel_name: string;
  check_date: string;
  technician_name: string;
  summary_ok: number;
  summary_not_ok: number;
}

const RoutineCheckHistory: React.FC = () => {
  const navigate = useNavigate();
  const { vesselId } = useParams<{ vesselId: string }>();
  const [lang, setLang] = useState<'el' | 'en'>(
    (sessionStorage.getItem('yacht_lang') as 'el' | 'en') || 'el'
  );
  const [checks, setChecks] = useState<CheckRow[]>([]);
  const [vesselName, setVesselName] = useState('');
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    sessionStorage.setItem('yacht_lang', lang);
  }, [lang]);

  useEffect(() => {
    const load = async () => {
      try {
        const vRes = await fetch('/api/vessels.php');
        if (vRes.ok) {
          const vd = await vRes.json();
          const list = vd.vessels || vd || [];
          const v = list.find((x: any) => String(x.id) === String(vesselId));
          if (v) setVesselName(v.name);
        }
        const cRes = await fetch(`/api/routine-checks.php?vessel_id=${vesselId}`);
        if (cRes.ok) {
          const cd = await cRes.json();
          setChecks(cd.checks || cd || []);
        }
      } catch (e) {
        console.error('Error loading history:', e);
      } finally {
        setLoading(false);
      }
    };
    if (vesselId) load();
  }, [vesselId]);

  const t = (el: string, en: string) => (lang === 'el' ? el : en);

  const fmtDate = (iso: string) => {
    if (!iso) return '-';
    const d = new Date(iso);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const filtered = checks.filter((c) => {
    if (!c.check_date) return false;
    const d = c.check_date.slice(0, 10);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#f3f4f6]">
      <div className="bg-[#1e40af] p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-[#1e3a8a] rounded-lg transition-colors">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">
                📋 {t('Ιστορικό Ελέγχων', 'Check History')}
              </h1>
              <p className="text-[#dbeafe] text-sm">
                {vesselName ? `🛥️ ${vesselName}` : t('Φόρτωση...', 'Loading...')}
              </p>
            </div>
          </div>
          <button
            onClick={() => setLang(lang === 'el' ? 'en' : 'el')}
            className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm text-white transition-colors"
          >
            {lang === 'el' ? 'EN' : 'EL'}
          </button>
        </div>
      </div>

      <div className="p-4 bg-white border-b border-[#d1d5db] shadow-sm">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-[#374151] font-medium mb-1">
              {t('Από', 'From')}
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-[#d1d5db] rounded-lg text-[#374151] text-sm focus:ring-2 focus:ring-[#1e40af] focus:border-[#1e40af] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-[#374151] font-medium mb-1">
              {t('Έως', 'To')}
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-[#d1d5db] rounded-lg text-[#374151] text-sm focus:ring-2 focus:ring-[#1e40af] focus:border-[#1e40af] focus:outline-none"
            />
          </div>
        </div>
        {(dateFrom || dateTo) && (
          <button
            onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="mt-2 text-xs text-[#1e40af] hover:underline"
          >
            {t('Καθαρισμός φίλτρων', 'Clear filters')}
          </button>
        )}
      </div>

      <div className="p-4">
        {loading ? (
          <p className="text-center text-gray-500 py-8">{t('Φόρτωση...', 'Loading...')}</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            {t('Δεν βρέθηκαν έλεγχοι', 'No checks found')}
          </p>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#f3f4f6] border-b border-[#d1d5db]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#374151] uppercase">{t('Ημερομηνία', 'Date')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#374151] uppercase">{t('Τεχνικός', 'Technician')}</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-[#374151] uppercase">OK</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-[#374151] uppercase">NOT OK</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[#374151] uppercase">{t('Ενέργειες', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} className="border-b border-[#e5e7eb] hover:bg-[#f9fafb] transition-colors">
                      <td className="px-4 py-3 text-sm text-[#374151] font-medium">{fmtDate(c.check_date)}</td>
                      <td className="px-4 py-3 text-sm text-[#6b7280]">{c.technician_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-center">
                        <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                          {c.summary_ok || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        <span className="inline-block px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">
                          {c.summary_not_ok || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => navigate(`/routine-check/view/${c.id}`)}
                          className="px-3 py-1 bg-[#1e40af] hover:bg-[#1e3a8a] text-white text-sm rounded-lg transition-colors"
                        >
                          {t('Προβολή', 'View')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoutineCheckHistory;
